import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, formatBRL } from "../../lib/api";
import { hourBankName, economia, canonicalCategory } from "../../lib/hourbank";
import { CheckCircle2, ArrowRight, Clock, Calendar, ShoppingBag, Zap, CreditCard } from "lucide-react";
import { toast } from "sonner";

const METHODS = [
  { id: "pix", label: "PIX", icon: Zap },
  { id: "cartao", label: "Cartão de crédito", icon: CreditCard },
];

export default function ComprarPacote() {
  const nav = useNavigate();
  const location = useLocation();
  const [plans, setPlans] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [method, setMethod] = useState("pix");
  const [loading, setLoading] = useState(false);
  const [highlightedPlanId, setHighlightedPlanId] = useState(null);
  const cardRefs = useRef({});

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const planId = params.get("plan_id");
    if (planId) {
      setHighlightedPlanId(planId);
      setTimeout(() => {
        const el = cardRefs.current[planId];
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 400);
    }
  }, [location.search]);

  useEffect(() => {
    api.get("/public/plans")
      .then(r => setPlans(r.data.filter(p => p.pricing_mode === "package" && (p.price_package || 0) > 0)))
      .catch(() => {});
    api.get("/public/spaces").then(r => setSpaces(r.data)).catch(() => {});
  }, []);

  const buy = async (planId) => {
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await api.post("/packages/purchase", { plan_id: planId, method });
      toast.success("Cobrança criada! Abrindo o pagamento…");
      if (data.payment_link) window.open(data.payment_link, "_blank");
      setTimeout(() => nav(`/portal/faturas?awaiting=${data.invoice_id}`), 600);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Erro ao gerar cobrança"));
    } finally {
      setLoading(false);
    }
  };

  const grouped = plans.reduce((acc, p) => {
    const k = canonicalCategory(p);
    (acc[k] = acc[k] || []).push(p);
    return acc;
  }, {});
  Object.values(grouped).forEach(list => list.sort((a, b) => (a.package_hours || 999) - (b.package_hours || 999)));

  return (
    <div data-testid="comprar-pacote-page">
      <div className="mb-6 sm:mb-8">
        <div className="text-xs uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-2">Comprar pacote</div>
        <h1 className="font-display text-2xl sm:text-3xl text-[#14213D] font-light">Escolha seu pacote de horas</h1>
        <p className="text-sm text-gray-500 mt-1">Pagamento via PIX (sem taxa) ou Cartão de crédito. Pacote ativado automaticamente após confirmação.</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-5 mb-6 flex items-center justify-between gap-3 overflow-x-auto" data-testid="purchase-stepper">
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="w-7 h-7 rounded-full bg-[#D4AF37] text-[#14213D] flex items-center justify-center text-xs font-bold">1</div>
          <span className="text-sm font-semibold text-[#14213D] whitespace-nowrap">Escolher pacote</span>
        </div>
        <div className="flex-1 h-px bg-gray-200" />
        <div className="flex items-center gap-2 flex-shrink-0 opacity-60">
          <div className="w-7 h-7 rounded-full border-2 border-gray-300 text-gray-400 flex items-center justify-center text-xs font-bold">2</div>
          <span className="text-sm text-gray-500 whitespace-nowrap">Pagamento</span>
        </div>
        <div className="flex-1 h-px bg-gray-200" />
        <div className="flex items-center gap-2 flex-shrink-0 opacity-60">
          <div className="w-7 h-7 rounded-full border-2 border-gray-300 text-gray-400 flex items-center justify-center text-xs font-bold">3</div>
          <span className="text-sm text-gray-500 whitespace-nowrap">Ativação automática</span>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="text-sm font-semibold text-[#14213D]">Forma de pagamento:</div>
        <div className="flex gap-2 flex-wrap">
          {METHODS.map(m => (
            <button
              key={m.id}
              onClick={() => setMethod(m.id)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition inline-flex items-center gap-1.5 ${method === m.id ? "bg-[#14213D] text-white border-[#14213D]" : "bg-white text-[#14213D] border-gray-300 hover:border-[#14213D]"}`}
              data-testid={`buy-method-${m.id}`}
            ><m.icon size={12} /> {m.label}</button>
          ))}
        </div>
      </div>

      {Object.keys(grouped).length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-400">
          Nenhum pacote disponível no momento.
        </div>
      ) : (
        Object.entries(grouped).map(([cat, list]) => (
          <div key={cat} className="mb-8">
            <h2 className="font-display text-xl text-[#14213D] mb-3">{cat}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {list.map((p) => {
                const isHL = highlightedPlanId === p.id;
                return (
                  <div
                    key={p.id}
                    ref={(el) => { cardRefs.current[p.id] = el; }}
                    className={`bg-white rounded-lg border p-5 flex flex-col transition ${isHL ? "border-[#D4AF37] ring-2 ring-[#D4AF37]/30 shadow-lg" : p.highlight ? "border-[#D4AF37]" : "border-gray-200"}`}
                    data-testid={`comprar-card-${p.id}`}
                  >
                    {isHL && <div className="inline-flex self-start mb-2 px-2 py-0.5 bg-[#D4AF37] text-[#14213D] text-[10px] uppercase tracking-wider font-bold rounded-full">Recomendado para você</div>}
                    {!isHL && p.package_hours === 32 && <div className="inline-flex self-start mb-2 px-2 py-0.5 bg-[#D4AF37] text-[#14213D] text-[10px] uppercase tracking-wider font-bold rounded-full">⭐ Mais vendido</div>}
                    <div className="font-display text-lg text-[#14213D]">{hourBankName(p)}</div>
                    <div className="text-xs text-gray-500 mt-1 mb-4">{p.description}</div>

                    <div className="flex items-baseline gap-1 mb-1">
                      <span className="font-display text-3xl text-[#14213D]">{formatBRL(p.price_package)}</span>
                    </div>
                    {(() => {
                      const eco = economia(p, spaces);
                      if (!eco) return null;
                      return (
                        <div className="mb-2 text-xs space-y-0.5" data-testid={`eco-${p.id}`}>
                          <div className="text-gray-400">Valor avulso: <s>{formatBRL(eco.avulso)}</s></div>
                          <div className="text-emerald-700 font-bold">Você economiza {formatBRL(eco.save)}</div>
                        </div>
                      );
                    })()}
                    <div className="flex items-center gap-3 text-[11px] text-gray-500 mb-4">
                      <span className="inline-flex items-center gap-1"><Clock size={11} /> {p.package_hours}h</span>
                      <span className="inline-flex items-center gap-1"><Calendar size={11} /> {p.validity_days || 30} dias</span>
                    </div>
                    <ul className="space-y-1.5 mb-5 flex-1">
                      {(p.features || []).slice(0, 4).map((f, i) => (
                        <li key={f || i} className="flex gap-2 text-xs text-gray-600">
                          <CheckCircle2 size={12} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => buy(p.id)}
                      disabled={loading}
                      className="w-full bg-[#14213D] hover:bg-[#1F335E] text-white px-4 py-2.5 rounded text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid={`buy-btn-${p.id}`}
                    >
                      <ShoppingBag size={14} /> Comprar agora <ArrowRight size={12} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
